import Layout from './Layout';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io as socketIO } from 'socket.io-client';

// API Config
const API_BASE = '/api/v1/marketing';
const WA_LINK_BASE = '/api/v1/wa-link';
// Socket.IO connects directly to the wa-linker service port
// If behind Apache proxy, use: const WA_SOCKET_URL = window.location.origin;
// If direct: const WA_SOCKET_URL = 'https://agents.matix.one:3005';
const WA_SOCKET_URL = window.location.origin; // Adjust if needed

const getToken = () => localStorage.getItem('token');

const api = {
  async importWhatsApp(phoneNumber, contacts, sourceType, groupsImported = []) {
    const res = await fetch(`${API_BASE}/contacts/import-whatsapp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
      body: JSON.stringify({ phone_number: phoneNumber, contacts, source_type: sourceType, groups_imported: groupsImported })
    });
    return res.json();
  },
  async waStatus() {
    const res = await fetch(`${WA_LINK_BASE}/status`, { headers: { 'Authorization': `Bearer ${getToken()}` } });
    return res.json();
  },
  async waConnect() {
    const res = await fetch(`${WA_LINK_BASE}/connect`, { method: 'POST', headers: { 'Authorization': `Bearer ${getToken()}` } });
    return res.json();
  },
  async waDisconnect() {
    const res = await fetch(`${WA_LINK_BASE}/disconnect`, { method: 'POST', headers: { 'Authorization': `Bearer ${getToken()}` } });
    return res.json();
  },
  async waChats() {
    const res = await fetch(`${WA_LINK_BASE}/chats`, { headers: { 'Authorization': `Bearer ${getToken()}` } });
    return res.json();
  },
  async waGroups() {
    const res = await fetch(`${WA_LINK_BASE}/groups`, { headers: { 'Authorization': `Bearer ${getToken()}` } });
    return res.json();
  },
  async waGroupMembers(groupId) {
    const res = await fetch(`${WA_LINK_BASE}/groups/${encodeURIComponent(groupId)}/members`, { headers: { 'Authorization': `Bearer ${getToken()}` } });
    return res.json();
  },
  async waRefresh() {
    const res = await fetch(`${WA_LINK_BASE}/refresh`, { method: 'POST', headers: { 'Authorization': `Bearer ${getToken()}` } });
    return res.json();
  }
};

// Icons
const Icon = ({ name, size = 18 }) => {
  const icons = {
    qr: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="8" height="8" rx="1"/><rect x="14" y="2" width="8" height="8" rx="1"/><rect x="2" y="14" width="8" height="8" rx="1"/><rect x="14" y="14" width="4" height="4"/><line x1="22" y1="14" x2="22" y2="18"/><line x1="18" y1="22" x2="22" y2="22"/><rect x="5" y="5" width="2" height="2"/><rect x="17" y="5" width="2" height="2"/><rect x="5" y="17" width="2" height="2"/></svg>,
    link: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
    check: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
    x: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
    download: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
    upload: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
    users: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    chat: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
    trash: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
    search: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
    save: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>,
    phone: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.8.32 1.58.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c1.23.38 2.01.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
    whatsapp: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>,
    excel: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/></svg>,
    refresh: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>,
    group: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    selectAll: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 12l2 2 4-4"/></svg>,
  };
  return icons[name] || null;
};

function detectRegion(phone) {
  const d = String(phone).replace(/\D/g, '');
  if (d.startsWith('20')) return 'EG';
  if (d.startsWith('966')) return 'SA';
  if (d.startsWith('971')) return 'AE';
  if (d.startsWith('965')) return 'KW';
  if (d.startsWith('974')) return 'QA';
  if (d.startsWith('968')) return 'OM';
  if (d.startsWith('973')) return 'BH';
  if (d.startsWith('962')) return 'JO';
  return 'UN';
}

function isValidWANumber(phone) {
  const d = String(phone).replace(/\D/g, '');
  return d.length >= 10 && d.length <= 15;
}

const WhatsAppAudienceEditor = () => {
  const [tab, setTab] = useState('link');
  const [connStatus, setConnStatus] = useState('disconnected');
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [filter, setFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [linkedPhone, setLinkedPhone] = useState('');
  const [linkedName, setLinkedName] = useState('');
  const [progress, setProgress] = useState(null);
  const [showGroups, setShowGroups] = useState(false);
  const [minMembers, setMinMembers] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [toast, setToast] = useState(null);
  const [qrImage, setQrImage] = useState(null);
  const [fetchingData, setFetchingData] = useState(false);
  const [listName, setListName] = useState('');
  const socketRef = useRef(null);

  const selected = contacts.filter(c => c.selected).length;
  const stats = {
    total: contacts.length,
    chats: contacts.filter(c => c.source === 'chat').length,
    groups: contacts.filter(c => c.source === 'group').length,
    valid: contacts.filter(c => c.valid).length,
    invalid: contacts.filter(c => !c.valid).length,
  };

  const filtered = contacts.filter(c => {
    if (filter !== 'all' && c.source !== filter) return false;
    if (countryFilter !== 'all' && c.region !== countryFilter) return false;
    if (search && !c.phone.includes(search) && !(c.name || '').includes(search)) return false;
    return true;
  });

  // Get unique countries from contacts
  const countries = [...new Set(contacts.map(c => c.region))].sort();

  const notify = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Socket.IO connection
  useEffect(() => {
    const token = getToken();
    if (!token) return;

    const socket = socketIO(WA_SOCKET_URL, {
      path: '/wa-link-socket/socket.io', // Adjust based on your Apache proxy config
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 2000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('WA-Linker socket connected');
    });

    socket.on('wa:status', (data) => {
      setConnStatus(data.status);
      if (data.phone) setLinkedPhone(data.phone);
      if (data.name) setLinkedName(data.name);
      if (data.error) notify(data.error, 'error');
      if (data.status === 'connected') setQrImage(null);
    });

    socket.on('wa:qr', (data) => {
      setQrImage(data.qr);
      setConnStatus('qr');
    });

    socket.on('wa:fetching', (data) => {
      setFetchingData(true);
      setProgress({ step: data.step === 'start' ? 1 : data.step === 'groups' ? 2 : 3, total: 3, label: data.label });
    });

    socket.on('wa:data-ready', (data) => {
      setFetchingData(false);
      setProgress(null);
      notify(`Data ready: ${data.chatsCount} chats, ${data.groupsCount} groups`);
    });

    socket.on('wa:error', (data) => {
      notify(data.message, 'error');
      setFetchingData(false);
      setProgress(null);
    });

    socket.on('connect_error', (err) => {
      console.error('Socket error:', err.message);
    });

    // Check initial status via REST
    api.waStatus().then(res => {
      if (res.success) {
        setConnStatus(res.status);
        if (res.phone) setLinkedPhone(res.phone);
        if (res.name) setLinkedName(res.name);
      }
    }).catch(() => {});

    return () => {
      socket.disconnect();
    };
  }, []);

  // Connect handler
  const handleConnect = async () => {
    setConnStatus('connecting');
    setQrImage(null);
    try {
      if (socketRef.current) {
        socketRef.current.emit('wa:connect');
      } else {
        await api.waConnect();
      }
    } catch (e) {
      notify('Connection error: ' + e.message, 'error');
      setConnStatus('disconnected');
    }
  };

  // Disconnect handler
  const handleDisconnect = async () => {
    try {
      if (socketRef.current) {
        socketRef.current.emit('wa:disconnect');
      } else {
        await api.waDisconnect();
      }
      setConnStatus('disconnected');
      setLinkedPhone('');
      setLinkedName('');
      setQrImage(null);
      setContacts([]);
      setGroups([]);
      notify('Disconnected');
    } catch (e) {
      notify('Error: ' + e.message, 'error');
    }
  };

  // Add contacts helper
  const addContacts = useCallback((newOnes, source) => {
    setContacts(prev => {
      const existing = new Set(prev.map(c => c.phone));
      const fresh = newOnes
        .filter(c => c.phone && !existing.has(c.phone))
        .map((c, i) => ({
          ...c,
          id: Date.now() + i + Math.random(),
          source: source || c.source || 'chat',
          contact: !!c.name || !!c.isContact,
          contactName: c.name || '',
          waName: c.waName || c.name || '',
          valid: isValidWANumber(c.phone),
          region: detectRegion(c.phone),
          selected: false
        }));
      return [...prev, ...fresh];
    });
  }, []);

  // Import chats - ONLY contacts WITH existing chat history (re-engagement)
  const importChats = async () => {
    if (connStatus !== 'connected') return;
    setProgress({ step: 1, total: 2, label: 'Importing contacts with chat history...' });
    try {
      const res = await api.waChats();
      if (res.success) {
        // Filter: ONLY contacts with chat history, valid numbers, no duplicates
        const withChat = res.chats.filter(c => c.hasChat && isValidWANumber(c.phone));
        const seen = new Set();
        const unique = withChat.filter(c => {
          const clean = c.phone.replace(/\D/g, '');
          if (seen.has(clean)) return false;
          seen.add(clean);
          return true;
        });
        addContacts(unique, 'chat');
        setProgress({ step: 2, total: 2, label: `Done! ${unique.length} contacts with chat history` });
        setTimeout(() => { setProgress(null); setTab('audience'); notify(`Imported ${unique.length} contacts with chat history`); }, 1200);
      } else {
        notify(res.message || 'Error', 'error');
        setProgress(null);
      }
    } catch (e) {
      notify('Error: ' + e.message, 'error');
      setProgress(null);
    }
  };

  // Fetch groups list for selection
  const openGroupSelector = async () => {
    if (connStatus !== 'connected') return;
    try {
      const res = await api.waGroups();
      if (res.success) {
        setGroups(res.groups.map(g => ({ ...g, selected: true })));
        setShowGroups(true);
      } else {
        notify(res.message || 'Error', 'error');
      }
    } catch (e) {
      notify('Error: ' + e.message, 'error');
    }
  };

  // Import selected groups members
  const importGroups = async () => {
    const selectedGroups = groups.filter(g => g.selected);
    if (!selectedGroups.length) { notify('No groups selected', 'error'); return; }
    setShowGroups(false);
    setProgress({ step: 0, total: selectedGroups.length, label: 'Importing groups...' });

    let totalImported = 0;
    for (let i = 0; i < selectedGroups.length; i++) {
      setProgress({ step: i + 1, total: selectedGroups.length, label: `Importing: ${selectedGroups[i].name}...` });
      try {
        const res = await api.waGroupMembers(selectedGroups[i].id);
        if (res.success && res.participants) {
          const members = res.participants
            .filter(p => p.phone !== '+' + linkedPhone) // Exclude self
            .map(p => ({
              phone: p.phone,
              name: p.name || '',
              source: 'group',
              groupName: selectedGroups[i].name
            }));
          addContacts(members, 'group');
          totalImported += members.length;
        }
      } catch (e) {
        console.error('Group import error:', e);
      }
    }

    setProgress({ step: selectedGroups.length, total: selectedGroups.length, label: `Done! ${totalImported} members` });
    setTimeout(() => { setProgress(null); setTab('audience'); notify(`Imported ${totalImported} members from ${selectedGroups.length} groups`); }, 800);
  };

  // Import all (chats + all groups)
  const importAll = async () => {
    if (connStatus !== 'connected') return;
    setProgress({ step: 1, total: 3, label: 'Importing chats...' });

    try {
      // 1. Import chats (ONLY with chat history)
      const chatsRes = await api.waChats();
      if (chatsRes.success) {
        const withChat = chatsRes.chats.filter(c => c.hasChat && isValidWANumber(c.phone));
        const seen = new Set();
        const unique = withChat.filter(c => {
          const clean = c.phone.replace(/\D/g, '');
          if (seen.has(clean)) return false;
          seen.add(clean);
          return true;
        });
        addContacts(unique, 'chat');
      }

      setProgress({ step: 2, total: 3, label: 'Importing groups...' });

      // 2. Import all group members
      const groupsRes = await api.waGroups();
      if (groupsRes.success) {
        let totalMembers = 0;
        for (const g of groupsRes.groups) {
          try {
            const membersRes = await api.waGroupMembers(g.id);
            if (membersRes.success && membersRes.participants) {
              const members = membersRes.participants
                .filter(p => p.phone !== '+' + linkedPhone)
                .map(p => ({ phone: p.phone, name: p.name || '', source: 'group', groupName: g.name }));
              addContacts(members, 'group');
              totalMembers += members.length;
            }
          } catch (e) { console.error('Group error:', e); }
        }
      }

      setProgress({ step: 3, total: 3, label: 'Done!' });
      setTimeout(() => { setProgress(null); setTab('audience'); notify('All data imported'); }, 800);
    } catch (e) {
      notify('Error: ' + e.message, 'error');
      setProgress(null);
    }
  };

  // Save ONLY filtered contacts to marketing DB
  const handleSave = async () => {
    if (!filtered.length) { notify('No contacts to save', 'error'); return; }
    setSaving(true);
    setSaveResult(null);
    try {
      const hasChat = filtered.some(c => c.source === 'chat');
      const hasGroup = filtered.some(c => c.source === 'group');
      const sourceType = hasChat && hasGroup ? 'all' : hasChat ? 'chats' : 'groups';

      // Auto-generate list name: phone + country + date
      const countryName = countryFilter !== 'all' ? countryFilter : 'ALL';
      const autoName = listName || `[${linkedPhone}] ${countryName} ${new Date().toLocaleDateString('ar-EG')}`;

      const result = await api.importWhatsApp(
        linkedPhone,
        filtered.map(c => ({ name: c.name || c.waName || '', phone: c.phone, company_name: '' })),
        sourceType,
        groups.filter(g => g.selected).map(g => g.name)
      );

      if (result.success) {
        setSaveResult(result.data);
        notify(`Saved! ${autoName} (${result.data.valid_contacts} valid)`);
      } else {
        notify(result.error || 'Save error', 'error');
      }
    } catch (e) {
      notify('Error: ' + e.message, 'error');
    }
    setSaving(false);
  };

  const toggleAll = () => {
    const ids = new Set(filtered.map(c => c.id));
    const allSel = filtered.every(c => c.selected);
    setContacts(contacts.map(c => ids.has(c.id) ? { ...c, selected: !allSel } : c));
  };

  return (
    <Layout>
    <div style={S.wrap}>
      <style>{CSS}</style>

      {/* Toast */}
      {toast && (
        <div style={{ ...S.toast, background: toast.type === 'error' ? '#fee2e2' : '#dcfce7', color: toast.type === 'error' ? '#dc2626' : '#16a34a', border: `1px solid ${toast.type === 'error' ? '#fca5a5' : '#86efac'}` }}>
          <Icon name={toast.type === 'error' ? 'x' : 'check'} size={16} /> {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={S.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={S.waLogo}><Icon name="whatsapp" size={22} /></div>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>WhatsApp Import</h1>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: '#64748b' }}>Link WhatsApp Web, import contacts and groups</p>
          </div>
        </div>
        <div style={{ ...S.badge, background: connStatus === 'connected' ? '#dcfce7' : connStatus === 'qr' || connStatus === 'connecting' ? '#fef3c7' : '#fee2e2', color: connStatus === 'connected' ? '#16a34a' : connStatus === 'qr' || connStatus === 'connecting' ? '#d97706' : '#ef4444' }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: connStatus === 'connected' ? '#22c55e' : connStatus === 'qr' || connStatus === 'connecting' ? '#f59e0b' : '#ef4444' }} />
          {connStatus === 'connected' ? `Connected (${linkedPhone})` : connStatus === 'qr' ? 'Scan QR...' : connStatus === 'connecting' ? 'Connecting...' : 'Disconnected'}
        </div>
      </div>

      {/* Tabs */}
      <div style={S.tabs}>
        <button style={{ ...S.tabBtn, ...(tab === 'link' ? S.tabAct : {}) }} onClick={() => setTab('link')}><Icon name="qr" size={16} /> Link WhatsApp</button>
        <button style={{ ...S.tabBtn, ...(tab === 'audience' ? S.tabAct : {}) }} onClick={() => setTab('audience')}>
          <Icon name="users" size={16} /> Audience
          {contacts.length > 0 && <span style={S.tabCount}>{contacts.length}</span>}
        </button>
      </div>

      {/* TAB 1: Link */}
      {tab === 'link' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div style={S.card}>
            <h3 style={S.cardH}><Icon name="qr" size={20} /> Scan QR Code</h3>
            <p style={{ margin: '0 0 24px', fontSize: 13, color: '#94a3b8', lineHeight: 1.6 }}>
              Open WhatsApp &gt; Settings &gt; Linked Devices &gt; Link a Device
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', margin: '24px 0' }}>
              {connStatus === 'connected' ? (
                <div style={{ textAlign: 'center', padding: 24 }}>
                  <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#dcfce7', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#16a34a' }}>
                    <Icon name="check" size={40} />
                  </div>
                  <h4 style={{ margin: '12px 0 4px', fontSize: 18, color: '#16a34a' }}>Connected!</h4>
                  <p style={{ color: '#64748b', fontSize: 13 }}>{linkedName} ({linkedPhone})</p>
                </div>
              ) : qrImage ? (
                <div style={{ position: 'relative', display: 'inline-block', borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
                  <img src={qrImage} alt="QR Code" style={{ display: 'block', width: 280, height: 280, borderRadius: 12 }} />
                </div>
              ) : connStatus === 'connecting' ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <div className="qr-spin" style={{ margin: '0 auto 16px' }} />
                  <p style={{ color: '#64748b', fontSize: 14 }}>Generating QR code...</p>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
                  <Icon name="qr" size={64} />
                  <p style={{ marginTop: 12 }}>Click "Link WhatsApp" to generate QR</p>
                </div>
              )}
            </div>
            {connStatus === 'disconnected' && (
              <button style={S.greenBtn} onClick={handleConnect}>
                <Icon name="link" size={16} /> Link WhatsApp Now
              </button>
            )}
            {connStatus === 'connected' && (
              <button style={S.redBtn} onClick={handleDisconnect}>
                <Icon name="x" size={16} /> Disconnect
              </button>
            )}
            {(connStatus === 'qr' || connStatus === 'connecting') && (
              <p style={{ textAlign: 'center', fontSize: 12, color: '#94a3b8', marginTop: 12 }}>
                Waiting for scan... QR refreshes automatically
              </p>
            )}
          </div>

          <div style={S.card}>
            <h3 style={S.cardH}><Icon name="download" size={20} /> Import Options</h3>
            {progress ? (
              <div style={{ padding: '32px 0' }}>
                <div style={{ height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', background: 'linear-gradient(90deg,#25d366,#128c7e)', borderRadius: 3, transition: 'width 0.5s', width: `${(progress.step / progress.total) * 100}%` }} />
                </div>
                <p style={{ textAlign: 'center', marginTop: 16, fontSize: 15, fontWeight: 600 }}>{progress.label}</p>
                <p style={{ textAlign: 'center', marginTop: 4, fontSize: 12, color: '#94a3b8' }}>Step {progress.step}/{progress.total}</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  { label: 'Import from Chats', desc: 'Individual conversations', bg: 'linear-gradient(135deg,#25d366,#128c7e)', icon: 'chat', fn: importChats },
                  { label: 'Import from Groups', desc: 'Group members', bg: 'linear-gradient(135deg,#8b5cf6,#7c3aed)', icon: 'group', fn: openGroupSelector },
                  { label: 'Import All', desc: 'Chats + Groups', bg: 'linear-gradient(135deg,#f59e0b,#d97706)', icon: 'download', fn: importAll },
                ].map((o, i) => (
                  <button key={i} disabled={connStatus !== 'connected'} onClick={o.fn}
                    style={{ ...S.impBtn, opacity: connStatus === 'connected' ? 1 : 0.5 }}>
                    <div style={{ width: 48, height: 48, borderRadius: 12, background: o.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}>
                      <Icon name={o.icon} size={24} />
                    </div>
                    <div><strong>{o.label}</strong><span style={{ display: 'block', fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{o.desc}</span></div>
                  </button>
                ))}
              </div>
            )}

            {connStatus === 'connected' && (
              <>
                <div style={{ height: 1, background: '#e2e8f0', margin: '20px 0' }} />
                <button style={{ ...S.smBtn, width: '100%', justifyContent: 'center' }} onClick={async () => {
                  try { await api.waRefresh(); notify('Data refreshed'); } catch (e) { notify('Error', 'error'); }
                }}>
                  <Icon name="refresh" size={14} /> Refresh WhatsApp Data
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Group Selector Modal */}
      {showGroups && (
        <div style={S.overlay} onClick={() => setShowGroups(false)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>Select Groups ({groups.length})</h3>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }} onClick={() => setShowGroups(false)}><Icon name="x" size={18} /></button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: 13, color: '#64748b', whiteSpace: 'nowrap' }}>Exclude groups with less than</span>
              <input type="number" min="0" value={minMembers} onChange={e => {
                const val = parseInt(e.target.value) || 0;
                setMinMembers(val);
                setGroups(prev => prev.map(g => ({ ...g, selected: g.members >= val })));
              }} style={{ width: 70, padding: '6px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, textAlign: 'center', outline: 'none' }} />
              <span style={{ fontSize: 13, color: '#64748b' }}>members</span>
              <span style={{ fontSize: 12, color: '#6366f1', fontWeight: 600, marginLeft: 'auto' }}>{groups.filter(g => g.members >= minMembers).length} groups selected</span>
            </div>
            <div style={{ padding: 20, overflowY: 'auto', flex: 1, maxHeight: '50vh' }}>
              {groups.length === 0 && <p style={{ color: '#94a3b8', textAlign: 'center' }}>No groups found</p>}
              {groups.map(g => (
                <label key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, cursor: 'pointer', marginBottom: 6, background: '#f8fafc' }}>
                  <input type="checkbox" checked={g.selected} onChange={() => setGroups(groups.map(x => x.id === g.id ? { ...x, selected: !x.selected } : x))} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{g.name}</div>
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>{g.members} members</div>
                  </div>
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 20px', borderTop: '1px solid #e2e8f0' }}>
              <button style={S.smBtn} onClick={() => setShowGroups(false)}>Cancel</button>
              <button style={{ ...S.smBtn, background: '#6366f1', color: '#fff', border: 'none' }} onClick={importGroups}>
                Import ({groups.filter(g => g.selected).length})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: Audience */}
      {tab === 'audience' && (
        <div>
          {/* Stats */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            {[
              { label: 'Total', val: stats.total, bg: '#eef2ff', color: '#4f46e5' },
              { label: 'Chats', val: stats.chats, bg: '#dbeafe', color: '#2563eb' },
              { label: 'Groups', val: stats.groups, bg: '#f3e8ff', color: '#7c3aed' },
              { label: 'Valid', val: stats.valid, bg: '#dcfce7', color: '#16a34a' },
              { label: 'Invalid', val: stats.invalid, bg: '#fee2e2', color: '#dc2626' },
              { label: 'Selected', val: selected, bg: '#fef3c7', color: '#d97706' },
            ].map((s, i) => (
              <div key={i} style={{ background: s.bg, color: s.color, padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, minWidth: 80, textAlign: 'center' }}>
                {s.val} <span style={{ fontWeight: 400, fontSize: 11 }}>{s.label}</span>
              </div>
            ))}
          </div>

          {/* Toolbar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 4, background: '#f1f0f7', borderRadius: 8, padding: 3 }}>
              {[{ k: 'all', l: 'All' }, { k: 'chat', l: 'Chats' }, { k: 'group', l: 'Groups' }].map(f => (
                <button key={f.k} onClick={() => setFilter(f.k)}
                  style={{ ...S.smBtn, border: 'none', background: filter === f.k ? '#fff' : 'transparent', boxShadow: filter === f.k ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
                  {f.l}
                </button>
              ))}
            </div>
            {/* Country Filter */}
            <select value={countryFilter} onChange={e => setCountryFilter(e.target.value)}
              style={{ padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', background: '#fff', cursor: 'pointer', color: '#475569', minWidth: 100 }}>
              <option value="all">All Countries</option>
              {countries.map(c => <option key={c} value={c}>{c} ({contacts.filter(x => x.region === c).length})</option>)}
            </select>

            <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
              <Icon name="search" size={14} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or phone..."
                style={{ width: '100%', padding: '8px 12px 8px 30px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit' }} />
            </div>
            <button style={S.smBtn} onClick={() => { setContacts(contacts.filter(c => !c.selected)); }}>
              <Icon name="trash" size={14} /> Delete Selected
            </button>
          </div>

          {/* Table */}
          <div style={{ background: '#fff', borderRadius: 14, overflow: 'auto', maxHeight: '55vh', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {[
                    { w: 40, c: <input type="checkbox" checked={filtered.length > 0 && filtered.every(c => c.selected)} onChange={toggleAll} /> },
                    { w: 50, c: '#' },
                    { c: 'Name' },
                    { c: 'Phone' },
                    { w: 70, c: 'Contact' },
                    { c: 'WA Name' },
                    { w: 60, c: 'Valid' },
                    { w: 60, c: 'Region' },
                    { w: 70, c: 'Source' }
                  ].map((h, i) => (
                    <th key={i} style={{ padding: '10px 12px', background: '#f1f0f7', color: '#64748b', fontWeight: 600, fontSize: 12, textAlign: 'left', borderBottom: '2px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 1, ...(h.w ? { width: h.w } : {}) }}>{h.c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((c, i) => (
                  <tr key={c.id} style={{ background: c.selected ? '#eef2ff' : i % 2 ? '#fafafa' : '#fff', transition: 'background 0.1s' }}>
                    <td style={S.td}><input type="checkbox" checked={c.selected} onChange={() => setContacts(contacts.map(x => x.id === c.id ? { ...x, selected: !x.selected } : x))} /></td>
                    <td style={{ ...S.td, color: '#94a3b8', fontSize: 12 }}>{i + 1}</td>
                    <td style={{ ...S.td, fontWeight: c.name ? 500 : 400, color: c.name ? '#1e293b' : '#cbd5e1' }}>{c.name || '\u2014'}</td>
                    <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12, direction: 'ltr' }}>{c.phone}</td>
                    <td style={S.td}>{c.contact ? <span style={{ color: '#22c55e' }}><Icon name="check" size={14} /></span> : <span style={{ color: '#ef4444' }}><Icon name="x" size={14} /></span>}</td>
                    <td style={{ ...S.td, color: c.waName ? '#475569' : '#cbd5e1' }}>{c.waName || '\u2014'}</td>
                    <td style={S.td}>{c.valid ? <span style={{ color: '#22c55e' }}><Icon name="check" size={14} /></span> : <span style={{ color: '#ef4444' }}><Icon name="x" size={14} /></span>}</td>
                    <td style={{ ...S.td, fontWeight: 600, fontSize: 12 }}>{c.region}</td>
                    <td style={S.td}><span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 6, background: c.source === 'chat' ? '#dbeafe' : '#f3e8ff', color: c.source === 'chat' ? '#2563eb' : '#7c3aed' }}>{c.source === 'chat' ? 'Chat' : 'Group'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!filtered.length && (
              <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>
                <Icon name="users" size={40} />
                <p>{contacts.length ? 'No results' : 'Link WhatsApp and import contacts'}</p>
              </div>
            )}
          </div>

          {/* Save Result */}
          {saveResult && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 12, padding: '14px 20px', marginTop: 14, color: '#16a34a' }}>
              <Icon name="check" size={18} />
              <div>
                <strong>Saved to database!</strong>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                  list_id: {saveResult.list_id} | {saveResult.list_name} | Total: {saveResult.total_contacts} | Valid: {saveResult.valid_contacts} | Invalid: {saveResult.invalid_contacts} | Duplicates: {saveResult.duplicate_contacts}
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', borderRadius: 14, padding: '14px 20px', marginTop: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 13, color: '#64748b' }}>List name:</span>
              <input value={listName} onChange={e => setListName(e.target.value)}
                style={{ padding: '8px 14px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', width: 280, outline: 'none' }}
                placeholder={`[${linkedPhone || 'WhatsApp'}] ${countryFilter !== 'all' ? countryFilter + ' ' : ''}${new Date().toLocaleDateString('ar-EG')}`} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button disabled={saving || !contacts.length} onClick={handleSave}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 24px', border: 'none', borderRadius: 8, background: 'linear-gradient(135deg,#25d366,#128c7e)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(37,211,102,0.25)', opacity: saving ? 0.7 : 1 }}>
                {saving ? <><div className="qr-spin" style={{ width: 16, height: 16, borderWidth: 2 }} /> Saving...</> : <><Icon name="save" size={14} /> Save ({filtered.length}{countryFilter !== 'all' ? ` - ${countryFilter}` : ''})</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </Layout>
  );
};

export default WhatsAppAudienceEditor;

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Arabic:wght@300;400;500;600;700&display=swap');
.qr-spin{width:40px;height:40px;border:3px solid #e2e8f0;border-top-color:#6366f1;border-radius:50%;animation:sp .8s linear infinite}
@keyframes sp{to{transform:rotate(360deg)}}
input[type=checkbox]{width:16px;height:16px;accent-color:#6366f1;cursor:pointer}
table tr:hover{background:#f1f0f7!important}
button:hover:not(:disabled){filter:brightness(.95)}
button:disabled{cursor:not-allowed;opacity:.5}
`;

const S = {
  wrap: { fontFamily: "'IBM Plex Sans Arabic',sans-serif", background: '#eeedf5', minHeight: '100vh', padding: 24, color: '#1e293b' },
  toast: { position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', padding: '10px 24px', borderRadius: 10, fontSize: 14, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8, zIndex: 9999, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  waLogo: { width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg,#25d366,#128c7e)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 4px 12px rgba(37,211,102,0.3)' },
  badge: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 16px', borderRadius: 20, fontSize: 13, fontWeight: 500 },
  tabs: { display: 'flex', gap: 4, background: '#f6f5fa', borderRadius: 12, padding: 4, marginBottom: 20, width: 'fit-content' },
  tabBtn: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 24px', border: 'none', borderRadius: 10, background: 'transparent', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: '#64748b', transition: 'all .2s', fontFamily: 'inherit' },
  tabAct: { background: '#fff', color: '#4f46e5', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  tabCount: { background: '#6366f1', color: '#fff', fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600 },
  card: { background: '#fff', borderRadius: 16, padding: 28, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  cardH: { display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 8px', fontSize: 17, fontWeight: 600 },
  greenBtn: { width: '100%', padding: '12px 24px', border: 'none', borderRadius: 10, background: 'linear-gradient(135deg,#25d366,#128c7e)', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontFamily: 'inherit' },
  redBtn: { width: '100%', padding: '12px 24px', border: '2px solid #fecaca', borderRadius: 10, background: '#fff', color: '#ef4444', fontSize: 14, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontFamily: 'inherit' },
  impBtn: { display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', border: '1.5px solid #e2e8f0', borderRadius: 12, background: '#fafafa', cursor: 'pointer', textAlign: 'left', transition: 'all .2s', fontFamily: 'inherit' },
  smBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', color: '#475569' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' },
  modal: { background: '#fff', borderRadius: 16, width: 480, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' },
  td: { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' },
};
